/**
 * Google Suggest JSONP – client-side requests (WordPress Keyword Tool compatible)
 *
 * Uses page protocol (http on localhost, https on production) to avoid Google blocking.
 * Params: callback, output, q, client, _ — no hl/gl (same as WP plugin).
 */

const MAX_CONCURRENT_JSONP = 5;

function getBaseUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  return `${protocol}//clients1.google.com/complete/search`;
}

function createCallbackName(): string {
  return `jQuery${Math.floor(Math.random() * 1e21)}_${Date.now()}`;
}

function buildUrl(callbackName: string, query: string): string {
  const base = getBaseUrl();
  return (
    `${base}?callback=${callbackName}` +
    `&output=json` +
    `&q=${encodeURIComponent(query)}` +
    `&client=firefox` +
    `&_=${Date.now()}`
  );
}

let activeRequests = 0;
const jsonpQueue: Array<() => void> = [];

function drainJsonpQueue(): void {
  while (activeRequests < MAX_CONCURRENT_JSONP && jsonpQueue.length > 0) {
    jsonpQueue.shift()?.();
  }
}

function jsonpFetch(query: string): Promise<string[]> {
  return new Promise((resolve, reject) => {
    const execute = () => {
      activeRequests += 1;

      const callbackName = createCallbackName();
      const url = buildUrl(callbackName, query);
      const script = document.createElement('script');

      const releaseQueue = () => {
        activeRequests -= 1;
        drainJsonpQueue();
      };

      const timeout = window.setTimeout(() => {
        cleanup();
        releaseQueue();
        reject(new Error(`Timeout: ${query}`));
      }, 15000);

      const cleanup = () => {
        window.clearTimeout(timeout);
        try {
          delete (window as unknown as Record<string, unknown>)[callbackName];
        } catch {
          (window as unknown as Record<string, unknown>)[callbackName] = undefined;
        }
        script.remove();
      };

      (window as unknown as Record<string, (data: unknown) => void>)[callbackName] = (data) => {
        cleanup();
        releaseQueue();
        const parsed = data as [string, string[]];
        resolve(Array.isArray(parsed?.[1]) ? parsed[1] : []);
      };

      script.onerror = () => {
        cleanup();
        releaseQueue();
        reject(new Error(`JSONP failed: ${query}`));
      };

      script.src = url;
      document.body.appendChild(script);
    };

    jsonpQueue.push(execute);
    drainJsonpQueue();
  });
}

export async function fetchGoogleSuggestions(query: string): Promise<string[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];

  try {
    return await jsonpFetch(trimmed);
  } catch (error) {
    console.warn('[GoogleSuggest]', trimmed, error);
    return [];
  }
}
